import { inject } from '@angular/core';
import { type HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { catchError, throwError, EMPTY, from, switchMap } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { ToastService } from '../../shared/components/toast.component';
import { NetworkService } from '../services/network.service';

interface ErrorResponseBody {
  requestId?: string;
  error?: {
    code?: string;
    customerMessage?: {
      title: string;
      message: string;
      alertType: 'ERROR' | 'WARNING' | 'INFO';
    };
  };
}

/**
 * Mobile error interceptor.
 * - Does NOT retry (SyncQueue handles retries for offline operations)
 * - Shows toast for online API calls that fail
 * - Handles auth token expiry
 * - Network errors are silenced if device is offline (expected behavior)
 *
 * RESUME-SAFE: This interceptor NEVER calls auth.signOut() directly.
 * The AuthService handles sign-out decisions internally, respecting the
 * resume grace period to avoid signing out when Keychain/Keystore is
 * temporarily inaccessible after screen unlock.
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const toast = inject(ToastService);
  const network = inject(NetworkService);

  // Sync queue requests handle their own errors — don't show toasts
  if (req.headers.has('X-Sync-Queue')) {
    return next(req).pipe(
      catchError((error: HttpErrorResponse) => throwError(() => error)),
    );
  }

  // Requests that explicitly suppress toast (e.g., ensureInProgressStatus where
  // 409 is expected and handled by the caller)
  if (req.headers.has('X-Suppress-Toast')) {
    return next(req).pipe(
      catchError((error: HttpErrorResponse) => throwError(() => error)),
    );
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const body = error.error as ErrorResponseBody | undefined;

      // Network error while offline → silently ignore (expected)
      if (error.status === 0 && !network.isOnline()) {
        return throwError(() => error);
      }

      // Network error while ONLINE → could be:
      // 1. CORS blocking a 401 response (token expired, API Gateway returns 401 without CORS headers)
      // 2. Transient network glitch right after app resume (connections dropped during background)
      //
      // Strategy: Try to get a valid token. If getIdToken() returns null,
      // the AuthService already handled sign-out internally (respecting resume grace).
      // We just show a toast if the user is no longer authenticated.
      if (error.status === 0 && network.isOnline()) {
        return from(auth.getIdToken()).pipe(
          switchMap((token) => {
            if (!token) {
              // getIdToken() returned null — either:
              // a) AuthService signed out (session truly expired), or
              // b) Within resume grace period (session preserved, token temporarily unavailable)
              if (!auth.isAuthenticated()) {
                void toast.warning('Tu sesión expiró. Inicia sesión nuevamente.');
                return EMPTY;
              }
              // User is still "authenticated" (resume grace) — treat as transient error
              return throwError(() => error);
            }
            // Token exists but request still failed → genuine network error
            return throwError(() => error);
          }),
          catchError((innerError) => {
            // getIdToken() threw an error — check if user was signed out
            if (!auth.isAuthenticated()) {
              void toast.warning('Tu sesión expiró. Inicia sesión nuevamente.');
              return EMPTY;
            }
            // User still authenticated (resume grace) — propagate as network error
            return throwError(() => innerError === error ? error : innerError);
          }),
        );
      }

      // Auth token expired → attempt refresh, then retry the request.
      // AuthService.refreshSession() handles resume-safety internally:
      // - Won't sign out during resume grace period
      // - Uses exponential backoff for Keychain access
      if (
        error.status === 401 ||
        body?.error?.code === 'AUTH_TOKEN_EXPIRED' ||
        body?.error?.code === 'AUTH_TOKEN_INVALID'
      ) {
        return from(auth.refreshSession()).pipe(
          switchMap(() => from(auth.getIdToken())),
          switchMap((newToken) => {
            if (!newToken) {
              // Refresh didn't produce a token — check if signed out
              if (!auth.isAuthenticated()) {
                void toast.warning('Tu sesión expiró. Inicia sesión nuevamente.');
                return EMPTY;
              }
              // Still authenticated (resume grace) — can't retry without token
              return throwError(() => error);
            }
            // Retry the original request with the new token
            const retryReq = req.clone({
              setHeaders: { Authorization: `Bearer ${newToken}` },
            });
            return next(retryReq);
          }),
          catchError(() => {
            // Refresh threw — AuthService handled sign-out internally if appropriate
            if (!auth.isAuthenticated()) {
              void toast.warning('Tu sesión expiró. Inicia sesión nuevamente.');
              return EMPTY;
            }
            // Still authenticated (resume grace) — propagate original error
            return throwError(() => error);
          }),
        );
      }

      // 403 Forbidden
      if (error.status === 403) {
        void toast.error(body?.error?.customerMessage?.message ?? 'Sin permisos');
        return throwError(() => error);
      }

      // Customer message present → route by alertType
      if (body?.error?.customerMessage) {
        const cm = body.error.customerMessage;
        switch (cm.alertType) {
          case 'ERROR':
            void toast.error(cm.message);
            break;
          case 'WARNING':
            void toast.warning(cm.message);
            break;
          case 'INFO':
            void toast.info(cm.message);
            break;
        }
      } else if (error.status >= 500) {
        void toast.error('Error del servidor. Intenta de nuevo.', () => {
          // Retry: re-send the original request
          next(req).subscribe();
        });
      } else if (error.status === 0) {
        void toast.warning('Sin conexión. Los datos se sincronizarán al reconectar.');
      }

      return throwError(() => error);
    }),
  );
};
